import React from 'react';
import { fetcher } from '@weezevent/weezjs-core';
import PropTypes from 'prop-types';
import ExportModal from '../events/exportModal';
import { toast, Modal, Button, DropdownItem } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { useSegment } from '../tracking/trackingProvider';

export const useInterval = (callback, delay = null) => {
    const cb = React.useRef(null);
    React.useEffect(() => {
        cb.current = callback;
    }, [callback]);

    React.useEffect(() => {
        if (!delay) {
            return;
        }

        const iid = setInterval(cb.current, delay);
        return () => {
            clearInterval(iid);
        };
    }, [delay]);
};

export const useCheckDownload = (path, callback, onError, initial_delay = 1000) => {
    const { t } = useTranslation();
    // Ternary if we start with a path
    let [delay, setDelay] = React.useState(path ? initial_delay : null);

    // Set path if it's needed
    React.useEffect(() => {
        let _delay = path ? initial_delay : null;
        setDelay(_delay);
    }, [path, initial_delay]);
    const call = () => {
        fetcher
            .get(path)
            .then(({ response, status }) => {
                if (status === 200) {
                    if (response.status === 'done') {
                        setDelay(null);
                        toast.success(t('ticket.export.download_success'));
                        window.open(response.dest, '_blank');
                        return callback(null, response);
                    }
                }
            })
            .catch(e => {
                onError();
            });
    };
    useInterval(call, delay);
};

export default function ExportButton({
    path,
    withModal,
    events,
    organization,
    chart,
    inventory,
    choiceProcess,
    onFetchDownload,
    onDownloadLaunch,
    onDownloadPreparation,
    showButton,
    isModal = false,
    open = false,
    onClose = () => {},
    modalTitleTrans = 'ticket.export.export_title',
    modalBodyTrans = 'ticket.export.export_description',
    buttonTrans = 'ticket.export.download_export',
    eventIds,
    onError,
    filterData,
    matchingData,
    withCustomizableModal,
    modalContent,
    customTitle,
    customSubtitle,
    selectedPeriod,
    datesFormat = { start_date: date => moment(date), end_date: date => moment(date) },
    datesInQueryFilters,
    queryFilters,
    queryOrdering,
    enableEventsFilter,
    submitButtonDisabled
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const [exportUrl, setExportUrl] = React.useState(null);
    const [openModal, setOpen] = React.useState(false);
    const [openCustomModal, setOpenCustomModal] = React.useState(false);
    const [finished, setFinished] = React.useState(true);

    const modalTitle = t(modalTitleTrans);
    const modalBody = t(modalBodyTrans);
    const buttonLabel = t(buttonTrans);
    const extraData = React.useMemo(() => {
        let data = {};
        // extraData will be mostly filters values. In Nacre filters each key have Set values so we convert it in Array for API
        for (const [key, value] of Object.entries(filterData)) {
            if (!(value instanceof Set && value.size === 0)) {
                data[key] = value instanceof Set ? Array.from(value) : value;
            }
        }
        return data;
    }, [filterData]);

    const fetchDownload = React.useCallback(
        (dates = {}, callback = () => {}, events = []) => {
            const url = path ? path : organization ? `/ticket/organizations/${organization.id}/reports/export/` : `/ticket/accounting/export/`;

            let data = { event_ids: eventIds, ...extraData };

            const datesKeys = Object.keys(dates);
            const keys = Object.keys(datesFormat);

            if (enableEventsFilter && events.length > 0) {
                queryFilters += `event__in=${events.join(',')}&`;
            }
            datesKeys.forEach(key => {
                if (key === 'startDate') {
                    if (dates[key]?.isValid()) {
                        data[keys[0]] = datesFormat[keys[0]](dates[key]);
                        if (datesInQueryFilters) {
                            if (choiceProcess === 'events') {
                                queryFilters += `start_date_range_after=${data[keys[0]]}&`;
                            } else {
                                queryFilters += `order__sold_date_after=${data[keys[0]]}&`;
                            }
                        }
                    }
                }
                if (key === 'endDate') {
                    if (dates[key]?.isValid()) {
                        data[keys[1]] = datesFormat[keys[1]](dates[key]);
                        if (datesInQueryFilters) {
                            if (choiceProcess === 'events') {
                                queryFilters += `end_date_range_before=${data[keys[1]]}`;
                            } else {
                                queryFilters += `order__sold_date_before=${data[keys[1]]}`;
                            }
                        }
                    }
                }
            });

            callback(); //HideModal if there is one

            if (choiceProcess === 'lfp_export') {
                data['chart_uuid'] = chart?.uuid;
                data['inventory_uuid'] = inventory?.uuid;
            }

            if (choiceProcess === 'product_transaction_excel') {
                let dataExportProducts = onFetchDownload();
                data['attendees'] = dataExportProducts['attendees'];
                data['buyers'] = dataExportProducts['buyers'];
                data['all_events'] = false;
            } else if (choiceProcess === 'count_attendees_by_rate_excel') {
                const isAllEvents = events.length === 0;
                data['event_ids'] = isAllEvents ? [] : events;
                data['all_events'] = isAllEvents;
                data['include_deleted'] = true;
                data['include_rate_details'] = true;
            } else {
                data['all_events'] = true;
                data['include_deleted'] = true;
                data['include_rate_details'] = true;
            }

            let payload = {
                status: 'ready',
                process: choiceProcess,
                data: data
            };

            if (matchingData) {
                payload['details'] = { columns: matchingData };
            }
            if (queryFilters) {
                payload['filters'] = queryFilters;
            }

            if (queryOrdering) {
                payload['ordering'] = queryOrdering;
            }

            fetcher
                .post(url, { data: payload })
                .then(({ response, status }) => {
                    if (status !== 201) {
                        throw new Error('Export could not be created');
                    }
                    if (response.status === 'ready' && response.url) {
                        //Launch download here
                        setExportUrl(response.url);
                        setFinished(false);
                        toast.info(t('ticket.export.download_preparation'));
                        onDownloadPreparation();
                    }
                })
                .catch(e => {
                    toast.error(t('ticket.export.download_preparation_error'));
                });
        },
        [extraData, eventIds, organization, datesFormat, matchingData, choiceProcess, queryFilters, queryOrdering, path, onDownloadPreparation]
    );
    // If exportUrl is null useCheckDownload doesn't trigger the poller
    useCheckDownload(
        exportUrl,
        (error, result) => {
            if (result) {
                setFinished(true);
                onDownloadLaunch();
            }
        },
        onError,
        3000
    );

    const onCloseCustomModal = React.useCallback(() => {
        onClose();
        setOpenCustomModal(false);
    }, [onClose]);

    return (
        <React.Fragment>
            {withModal && (
                <ExportModal
                    openModal={openModal}
                    events={events}
                    organization={organization}
                    onClose={() => setOpen(false)}
                    onAccept={({ startDate, endDate, selected }) => fetchDownload({ startDate: startDate, endDate: endDate }, setOpen(false), selected)}
                    modalTitle={modalTitle}
                    modalBody={modalBody}
                    enableEventsFilter={enableEventsFilter}
                />
            )}
            {isModal && (
                <ExportModal
                    openModal={open}
                    organization={organization}
                    onClose={onClose}
                    onAccept={({ startDate, endDate, selected }) => fetchDownload({ startDate: startDate, endDate: endDate }, setOpen(false), selected)}
                    modalTitle={modalTitle}
                    modalBody={modalBody}
                    enableEventsFilter={enableEventsFilter}
                />
            )}
            {withCustomizableModal && (
                <Modal
                    open={open || openCustomModal}
                    organization={organization}
                    onClose={onCloseCustomModal}
                    title={customTitle}
                    subtitle={customSubtitle}
                    submitButton={
                        <Button
                            primary
                            label={t('ticket.common.action.download')}
                            disabled={submitButtonDisabled}
                            onClick={() => fetchDownload(selectedPeriod, onCloseCustomModal())}
                        />
                    }
                    cancelButton={<Button label={t('ticket.common.action.back')} onClick={onCloseCustomModal} />}
                >
                    {modalContent}
                </Modal>
            )}
            {!isModal && showButton && (
                <Button
                    primary
                    inverted
                    isLoading={!finished}
                    onClick={() => {
                        if (withModal) {
                            setOpen(true);
                        } else if (withCustomizableModal) {
                            setOpenCustomModal(true);
                        } else {
                            fetchDownload(selectedPeriod);
                        }
                    }}
                >
                    {buttonLabel}
                </Button>
            )}
            {!showButton && !isModal && !withCustomizableModal && (
                <DropdownItem
                    disabled={!finished}
                    onClick={() => {
                        if (choiceProcess === 'discount-codes') {
                            track('ACTION > EXPORT DISCOUNT', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO' });
                        }
                        if (choiceProcess === 'rates-excel') {
                            track('ACTION > EXPORT TICKET TYPE LIST', { 'Page Title': 'TICKET TYPES' });
                        }
                        return withModal ? setOpen(true) : fetchDownload();
                    }}
                    item={buttonLabel}
                />
            )}
        </React.Fragment>
    );
}

ExportButton.defaultProps = {
    path: null,
    withModal: false,
    events: {},
    onFetchDownload: () => {},
    onDownloadPreparation: () => {},
    onDownloadLaunch: () => {},
    onError: () => {},
    showButton: true,
    eventIds: [],
    filterData: {},
    matchingData: null,
    queryFilters: '',
    datesInQueryFilters: false,
    enableEventsFilter: false,
    submitButtonDisabled: false
};

ExportButton.propTypes = {
    path: PropTypes.string,
    withModal: PropTypes.bool,
    showButton: PropTypes.bool,
    organization: PropTypes.object.isRequired,
    events: PropTypes.object,
    choiceProcess: PropTypes.string.isRequired,
    onFetchDownload: PropTypes.func,
    onDownloadPreparation: PropTypes.func,
    onDownloadLaunch: PropTypes.func,
    onError: PropTypes.func,
    modalTitleTrans: PropTypes.string,
    modalBodyTrans: PropTypes.string,
    buttonTrans: PropTypes.string,
    eventIds: PropTypes.array,
    isModal: PropTypes.bool,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    filterData: PropTypes.object,
    queryFilters: PropTypes.string,
    datesInQueryFilters: PropTypes.bool,
    enableEventsFilter: PropTypes.bool,
    submitButtonDisabled: PropTypes.bool
};
